import { useMemo } from 'react';
import type { SeatDto, SeatZoneDto } from '../api';

/**
 * TICK-103 — the buyer's view of the room.
 *
 * Two things here are not styling decisions:
 *
 * - **Colour is never the only signal.** A taken seat is dimmed *and* hollow, a blocked seat is
 *   crossed, a selected seat carries a ring. Zone colour tells you the price band; it never tells
 *   you on its own whether you can sit somewhere, because roughly one man in twelve cannot read
 *   that difference reliably.
 * - **Every seat is a real button.** Keyboard users tab to a seat and press it; each carries a
 *   label naming the seat and its state, because "circle 47" is not a seat to a screen reader.
 *
 * The map is a snapshot, not a live feed (spec §12.5). It is honest about that by re-checking on
 * submit: the primary key on `order_seats` is what actually guarantees nobody double-books, so a
 * map a few seconds stale is a cosmetic problem rather than a correctness one.
 */

const CELL = 26;
/*
 * TICK-105 — a table is drawn bigger because it has to carry two things a chair does not: its code
 * and how many people it seats. A buyer choosing between "T-3" and "T-7" is choosing furniture, and
 * a square with nothing written on it does not tell them which one seats six.
 */
const TABLE_CELL = 46;
const GAP = 4;
const PAD = 28;

export interface SeatMapProps {
  zone: SeatZoneDto;
  /** TICK-105 — admissions per unit, from the zone's ticket type. 1 for a chair. */
  unitSeats?: number;
  seats: SeatDto[];
  selected: string[];
  onToggle: (seatId: string) => void;
  /** Reached the per-order limit — remaining free seats stop being offerable. */
  atLimit: boolean;
}

export function SeatMap({ zone, seats, selected, onToggle, atLimit, unitSeats = 1 }: SeatMapProps) {
  const isTable = zone.unitKind === 'table';
  const cell = isTable ? TABLE_CELL : CELL;
  const noun = isTable ? 'โต๊ะ' : 'ที่นั่ง';
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const { width, height, rows } = useMemo(() => {
    const maxX = seats.reduce((m, s) => Math.max(m, s.x), 0);
    const maxY = seats.reduce((m, s) => Math.max(m, s.y), 0);
    const byRow = new Map<number, { label: string }>();
    for (const s of seats) if (!byRow.has(s.y)) byRow.set(s.y, { label: s.rowLabel });
    return {
      width: PAD * 2 + (maxX + 1) * (cell + GAP),
      height: PAD * 2 + (maxY + 1) * (cell + GAP),
      rows: [...byRow.entries()].sort((a, b) => a[0] - b[0]),
    };
  }, [seats, cell]);

  if (!seats.length) return <div className="seatmap-empty">โซนนี้ยังไม่ได้สร้างผัง{noun}</div>;

  return (
    <div className="seatmap-wrap">
      <div className="seatmap-stage" aria-hidden="true">เวที / STAGE</div>
      {/* Wide venues scroll inside their own box rather than pushing the page sideways. */}
      <div className="seatmap-scroll">
        <svg
          className="seatmap-svg"
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          role="group"
          aria-label={`ผัง${noun}โซน ${zone.code}${zone.label ? ` ${zone.label}` : ''}`}
        >
          {rows.map(([y, row]) => (
            <text
              key={`row-${y}`}
              x={PAD - 12}
              y={PAD + y * (cell + GAP) + cell * 0.7}
              className="seatmap-rowlabel"
              textAnchor="end"
              aria-hidden="true"
            >
              {row.label}
            </text>
          ))}

          {seats.map((s) => {
            const isSelected = selectedSet.has(s.id);
            const unavailable = s.state !== 'available';
            // At the limit, a free seat you have not picked is not offerable — but a seat you
            // already picked must stay clickable, or there is no way to change your mind.
            const disabled = unavailable || (atLimit && !isSelected);
            const cx = PAD + s.x * (cell + GAP);
            const cy = PAD + s.y * (cell + GAP);
            const stateWord =
              s.state === 'blocked' ? 'ปิดการขาย' : s.state === 'taken' ? 'ถูกจองแล้ว' : isSelected ? 'เลือกอยู่' : 'ว่าง';

            return (
              <g key={s.id}>
                <rect
                  x={cx}
                  y={cy}
                  width={cell}
                  height={cell}
                  rx={6}
                  className={[
                    'seatmap-seat',
                    `is-${s.state}`,
                    isSelected ? 'is-selected' : '',
                    disabled ? 'is-disabled' : '',
                  ].filter(Boolean).join(' ')}
                  style={{ fill: s.state === 'available' || isSelected ? zone.color : undefined }}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-disabled={disabled}
                  aria-pressed={isSelected}
                  aria-label={
                    isTable
                      ? `โต๊ะ ${s.seatCode} นั่งได้ ${unitSeats} คน — ${stateWord}`
                      : `ที่นั่ง ${s.seatCode} — ${stateWord}`
                  }
                  onClick={() => { if (!disabled) onToggle(s.id); }}
                  onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(s.id); }
                  }}
                />
                {/* A table carries its own name and how many it seats; a chair has room for neither. */}
                {isTable && (
                  <g aria-hidden="true" className="seatmap-tablelabel">
                    <text x={cx + cell / 2} y={cy + cell * 0.42} textAnchor="middle">{s.seatCode}</text>
                    <text x={cx + cell / 2} y={cy + cell * 0.75} textAnchor="middle" className="is-capacity">
                      {unitSeats} ที่
                    </text>
                  </g>
                )}
                {/* Shape, not just colour: a blocked unit is crossed out. */}
                {s.state === 'blocked' && (
                  <path
                    d={`M${cx + 7} ${cy + 7} L${cx + cell - 7} ${cy + cell - 7} M${cx + cell - 7} ${cy + 7} L${cx + 7} ${cy + cell - 7}`}
                    className="seatmap-blocked-cross"
                    aria-hidden="true"
                  />
                )}
                {isSelected && (
                  <rect
                    x={cx - 3} y={cy - 3} width={cell + 6} height={cell + 6} rx={9}
                    className="seatmap-selected-ring" aria-hidden="true"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="seatmap-legend">
        <span><i className="seatmap-swatch" style={{ background: zone.color }} /> ว่าง</span>
        <span><i className="seatmap-swatch is-taken" /> ถูกจองแล้ว</span>
        <span><i className="seatmap-swatch is-blocked" /> ปิดการขาย</span>
      </div>
    </div>
  );
}
